import React, { useEffect, useLayoutEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import type { TransactionEvent, SpanJSON, Measurements } from '@sentry/core';
import { requestRateMonitor } from '@ais-ui/utils';
export { startMemoryDiagnostics } from './memoryDiagnosticsReporting';
import {
  getTraceSampleRate,
  IGNORED_RESOURCE_SPAN_OPS,
  shouldCreateSpanForRequest,
} from './tracesSampling';
import { createErrorThrottle } from './errorThrottling';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

interface NetworkInformation {
  effectiveType?: string;
  downlink?: number;
}

interface PerformanceMemory {
  jsHeapSizeLimit: number;
  usedJSHeapSize: number;
  totalJSHeapSize: number;
}

declare global {
  interface Navigator {
    connection?: NetworkInformation;
  }
  interface Performance {
    memory?: PerformanceMemory;
  }
}

// Patterns to downsample (keep ~15% to preserve trend visibility)
const DOWNSAMPLE_RATE = 0.15;
const DOWNSAMPLE_PATTERNS = [
  'WebSocket error: NotFound',
  'WebSocket closed unexpectedly',
  "Failed to execute 'drawImage'",
  'Service unavailable:',
];

/**
 * Builds Sentry `measurements` for the browser's JS heap readings.
 *
 * Returns `undefined` when `performance.memory` is unavailable (it is a
 * non-standard Chromium-only API) so callers can skip the key entirely
 * rather than reporting a misleading zero.
 *
 * Values are raw bytes with an explicit `byte` unit — Sentry handles the
 * display scaling, and keeping the raw value means aggregations stay exact.
 *
 * Exported for testing.
 */
export function buildMemoryMeasurements(): Measurements | undefined {
  const used = performance.memory?.usedJSHeapSize;
  const limit = performance.memory?.jsHeapSizeLimit;
  const total = performance.memory?.totalJSHeapSize;

  const measurements: Measurements = {};

  if (typeof used === 'number') {
    measurements.memory_used = { value: used, unit: 'byte' };
  }
  if (typeof total === 'number') {
    measurements.memory_total = { value: total, unit: 'byte' };
  }
  if (typeof limit === 'number') {
    measurements.memory_limit = { value: limit, unit: 'byte' };
  }

  // A heap approaching its ceiling is the actionable signal, and it is
  // cheaper to compute once here than to divide two measurements in every
  // query or alert rule.
  if (typeof used === 'number' && typeof limit === 'number' && limit > 0) {
    measurements.memory_used_ratio = { value: used / limit, unit: 'ratio' };
  }

  return Object.keys(measurements).length > 0 ? measurements : undefined;
}

/**
 * Initialize Sentry **before** React mounts so that
 * `browserTracingIntegration` captures a real `pageload` span and the
 * `tracesSampler` condition for `sentry.op === 'pageload'` actually fires.
 *
 * React Router integration is added later by the `useSentry` hook once
 * the Router context is available.
 */
export function initSentry(dsn: string, environment: string, release: string) {
  if (!dsn) {
    console.warn('Sentry DSN is not configured. Skipping initialization.');
    return;
  }

  if (!environment) {
    console.warn(
      'Sentry environment is not configured. Skipping initialization.',
    );
    return;
  }

  const shouldSendError = createErrorThrottle();

  Sentry.init({
    dsn,
    environment,
    release,
    integrations: [
      Sentry.browserTracingIntegration({
        shouldCreateSpanForRequest,
        ignoreResourceSpans: IGNORED_RESOURCE_SPAN_OPS,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        maskAllInputs: true,
        blockAllMedia: false,
      }),
      Sentry.feedbackIntegration({
        colorScheme: 'system',
        autoInject: false,
      }),
      Sentry.dedupeIntegration(),
    ],
    // Trace sampling policy lives in `tracesSampling.ts`: auth at 100%, page
    // loads at ~50%, the continuous map pollers (overlay / ship position /
    // track) at ~5%, other high-frequency polls and lookups at ~10%, and
    // everything else at 100% so meaningful transactions (navigations,
    // user-initiated fetches) are fully captured. Map tile requests and image
    // resources get no spans at all.
    tracesSampler: getTraceSampleRate,
    replaysSessionSampleRate: 0.01,
    replaysOnErrorSampleRate: 0.1,
    maxBreadcrumbs: 50,

    // Downsample high-frequency known errors, then report each distinct
    // message at most once per hour per tab (see errorThrottling.ts), to keep
    // repeating failures from exhausting the Sentry error quota.
    beforeSend(event: Sentry.ErrorEvent): Sentry.ErrorEvent | null {
      const message =
        event.message || event.exception?.values?.[0]?.value || '';

      const isNoisyEvent = DOWNSAMPLE_PATTERNS.some(
        (pattern) =>
          message === pattern ||
          message.startsWith(`${pattern}:`) ||
          message.startsWith(`${pattern} `),
      );

      if (isNoisyEvent && Math.random() > DOWNSAMPLE_RATE) {
        return null;
      }

      if (!shouldSendError(message)) {
        return null;
      }

      return event;
    },

    beforeSendTransaction(
      transaction: TransactionEvent,
    ): TransactionEvent | null {
      transaction.contexts = transaction.contexts || {};
      transaction.contexts.device = {
        ...transaction.contexts.device,
        screen_resolution: `${window.screen.width}x${window.screen.height}`,
        viewport_size: `${window.innerWidth}x${window.innerHeight}`,
      };

      // Memory goes in `measurements`, not `tags`. Tags are strings, so the
      // previous `'36MB'` form could only ever be grouped and counted — no
      // avg/max/p95, no time series, no alert rule. It also meant every
      // distinct megabyte value became its own tag value, which is exactly
      // the high-cardinality pattern tags are not meant for. Measurements are
      // numeric and unit-aware, so `max(measurements.memory_used)` and a
      // "heap above X" alert both become possible.
      //
      // `usedJSHeapSize` measures main-thread V8 heap, not worker heaps or
      // browser process footprint. Earlier small samples were not matched
      // to the large-tab incident and do not prove native-memory retention.
      // Compare measurements from the same diagnostic run before concluding.
      const memoryMeasurements = buildMemoryMeasurements();
      if (memoryMeasurements) {
        transaction.measurements = {
          ...transaction.measurements,
          ...memoryMeasurements,
        };
      }

      transaction.tags = {
        ...transaction.tags,
        'screen.resolution': `${window.screen.width}x${window.screen.height}`,
        'viewport.size': `${window.innerWidth}x${window.innerHeight}`,
        'screen.orientation': window.screen.orientation?.type || 'unknown',
        'device.pixel_ratio': window.devicePixelRatio,
        'device.type':
          window.innerWidth < 768
            ? 'mobile'
            : window.innerWidth < 1024
              ? 'tablet'
              : 'desktop',
        'device.is_mobile': window.innerWidth < 768,
        'browser.online': navigator.onLine,
        'browser.language': navigator.language,
        'connection.type': navigator.connection?.effectiveType || 'unknown',
        'connection.downlink': navigator.connection?.downlink || 'unknown',
      };

      return transaction;
    },
    beforeSendSpan(span: SpanJSON): SpanJSON {
      // Numeric span attributes are aggregatable in the spans dataset, so
      // these are stored as raw byte numbers rather than formatted strings.
      const usedBytes = performance.memory?.usedJSHeapSize;
      const limitBytes = performance.memory?.jsHeapSizeLimit;

      span.data = {
        ...span.data,
        'screen.resolution': `${window.screen.width}x${window.screen.height}`,
        'viewport.size': `${window.innerWidth}x${window.innerHeight}`,
        'device.type':
          window.innerWidth < 768
            ? 'mobile'
            : window.innerWidth < 1024
              ? 'tablet'
              : 'desktop',
        'browser.online': navigator.onLine,
        'connection.type': navigator.connection?.effectiveType || 'unknown',
        ...(typeof usedBytes === 'number' && {
          'memory.used_bytes': usedBytes,
        }),
        ...(typeof limitBytes === 'number' && {
          'memory.limit_bytes': limitBytes,
        }),
      };

      return span;
    },
  });

  // Monitor outgoing request rates and alert on runaway loops.
  // Threshold: >300 requests per 60s (5 req/s sustained) per category.
  requestRateMonitor.start((category, count, windowMs) => {
    const seconds = windowMs / 1000;
    Sentry.captureMessage(
      `High request rate: ${count} ${category} requests in ${seconds}s`,
      {
        level: 'warning',
        tags: {
          'request_monitor.category': category,
          'request_monitor.rate_per_second': String(
            (count / seconds).toFixed(1),
          ),
        },
        extra: {
          category,
          count,
          windowMs,
          ratePerSecond: Number((count / seconds).toFixed(1)),
        },
        fingerprint: ['request-rate-monitor', category],
      },
    );
  });
}

/**
 * Hook that adds React Router integration to Sentry after the Router
 * context is available. Must be called inside a component rendered within
 * `<Router>`.
 *
 * Uses useLayoutEffect to register the integration synchronously before
 * any useEffect-based navigations (e.g. AuthProvider session restore),
 * ensuring early route changes are captured by Sentry.
 */
export const useSentry = () => {
  useLayoutEffect(() => {
    const client = Sentry.getClient();
    if (!client) return;

    // Add React Router integration now that Router context is available
    client.addIntegration(
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
        shouldCreateSpanForRequest,
        ignoreResourceSpans: IGNORED_RESOURCE_SPAN_OPS,
      }),
    );
  }, []);
};

export const useSentryStatus = () => {
  const [isInitialized, setIsInitialized] = useState(() => {
    try {
      const client = Sentry.getClient();
      return !!client && !!client.getOptions();
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (isInitialized) return;

    const checkInterval = setInterval(() => {
      try {
        const client = Sentry.getClient();
        if (client && client.getOptions()) {
          setIsInitialized(true);
          clearInterval(checkInterval);
        }
      } catch {
        // Not ready yet
      }
    }, 100);

    return () => clearInterval(checkInterval);
  }, [isInitialized]);

  return isInitialized;
};
